import { inject } from '@angular/core';
import { TranslationService } from '@lib/i18n/data-access';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { ToastService } from '@platform-workspace/design-system-v2';
import { BaseResponse, CommunicationChannelDto } from '@shared/utils/interfaces';
import { hideLoading, showLoading, withFeatureLoading } from '@shared/utils/models';
import { EMPTY, exhaustMap, Observable, pipe, switchMap } from 'rxjs';

import { CommunicationChannelApiService } from '../../services/communication-channel-api/communication-channel-api.service';
import { AggregatorRegionsStore } from '../aggregator-regions/aggregator-regions.store';

export interface CommunicationChannelState {
  channels: CommunicationChannelDto[];
}

const initialState: CommunicationChannelState = {
  channels: [],
};

export const CommunicationChannelStore = signalStore(
  withState(initialState),
  withFeatureLoading(),
  withMethods(
    (
      store,
      toastService = inject(ToastService),
      translationService = inject(TranslationService),
      apiService = inject(CommunicationChannelApiService),
      aggregatorRegionsStore = inject(AggregatorRegionsStore),
    ) => ({
      loadChannels: rxMethod<string>(
        pipe(
          switchMap((entityId) => {
            const regionId = aggregatorRegionsStore.currentRegionId();

            if (!regionId) {
              return EMPTY;
            }

            patchState(store, showLoading());

            return apiService.getCommunicationChannels(regionId, entityId).pipe(
              tapResponse({
                next: (response) => {
                  patchState(store, { channels: response.payload || [] });
                },
                error: console.error,
                finalize: () => {
                  patchState(store, hideLoading());
                },
              }),
            );
          }),
        ),
      ),

      createCommunicationChannel(data: CommunicationChannelDto, entityId: string): Observable<BaseResponse<void>> {
        const regionId = aggregatorRegionsStore.currentRegionId();

        if (!regionId) {
          return EMPTY;
        }

        patchState(store, showLoading());

        return apiService.createCommunicationChannel(data, regionId, entityId).pipe(
          tapResponse({
            next: () => {
              toastService.showSuccess(translationService.translate('shared.communicationChannel.successCreate'));
            },
            error: console.error,
            finalize: () => {
              patchState(store, hideLoading());
            },
          }),
        );
      },

      updateCommunicationChannel(data: CommunicationChannelDto, entityId: string, channelId: string): Observable<BaseResponse<void>> {
        const regionId = aggregatorRegionsStore.currentRegionId();

        if (!regionId) {
          return EMPTY;
        }

        patchState(store, showLoading());
        return apiService.updateCommunicationChannel(data, regionId, entityId, channelId).pipe(
          tapResponse({
            next: () => {
              toastService.showSuccess(translationService.translate('shared.communicationChannel.successUpdate'));
            },
            error: console.error,
            finalize: () => {
              patchState(store, hideLoading());
            },
          }),
        );
      },

      deleteChannel: rxMethod<{ entityId: string; channelId: string }>(
        pipe(
          exhaustMap(({ entityId, channelId }) => {
            const regionId = aggregatorRegionsStore.currentRegionId();

            if (!regionId) {
              return EMPTY;
            }

            patchState(store, showLoading());

            return apiService.deleteCommunicationChannel(regionId, entityId, channelId).pipe(
              tapResponse({
                next: () => {
                  const updatedChannels = store.channels().filter((channel) => channel.id !== channelId);
                  patchState(store, { channels: updatedChannels });

                  toastService.showSuccess(translationService.translate('shared.communicationChannel.deleteSuccess'));
                },
                error: console.error,
                finalize: () => {
                  patchState(store, hideLoading());
                },
              }),
            );
          }),
        ),
      ),
    }),
  ),
);
